import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FaUsers, FaTrophy, FaPlus } from 'react-icons/fa';
import { IoGolfOutline, IoChevronForward } from 'react-icons/io5';
import { supabase } from '../ReusableComponents/supabaseClient';
import PoolDetailsModal from '../Pool/PoolDetailsModal';
import StatusBadge from '../ReusableComponents/StatusBadge';
import { toast } from 'react-toastify';
import { logError } from '../Utilities/errorLogger';
import { useAuth } from '../Utilities/AuthContext';

function Dashboard() {
  const [pools, setPools] = useState([]);
  const [teams, setTeams] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const { ownerData } = useAuth();
  const userId = ownerData?.userId || localStorage.getItem("userId");
  const userName = ownerData?.userName || localStorage.getItem("userName");
  const userEmail = ownerData?.email || localStorage.getItem("email");

  const fetchDashboardData = useCallback(async () => {
    if (!userId) return;

    try {
      const { data: poolsData, error: poolsError } = await supabase
        .from('InviteLink')
        .select(`
          id,
          pool (
            id, name, tournament_fixture,
            all_players_completed, tournament_status,
            tournamentsFixture:tournament_fixture ( id, tournament_id )
          )
        `)
        .or(`person.eq.${userId},email.eq.${userEmail}`)
        .order('created_date', { ascending: false })
        .limit(5);

      if (poolsError) throw poolsError;

      const transformedPools = poolsData?.map(item => ({ ...item.pool })) || [];

      // Fetch team counts for each pool
      const poolIds = [...new Set(transformedPools.map(p => p.id).filter(Boolean))];
      if (poolIds.length > 0) {
        const { data: teamCounts } = await supabase
          .from('team')
          .select('pool')
          .in('pool', poolIds);
        const countMap = {};
        (teamCounts || []).forEach(t => { countMap[t.pool] = (countMap[t.pool] || 0) + 1; });
        transformedPools.forEach(p => { p.teamCount = countMap[p.id] || 0; });
      }

      const { data: teamsData, error: teamsError } = await supabase
        .from('team')
        .select(`
          id, owner, pool, team_world_ranking_average, missed_cut,
          pool:pool (
            id, name, all_players_completed, tournament_fixture, tournament_status,
            tournamentsFixture:tournament_fixture ( id, tournament_id )
          )
        `)
        .or(`owner.eq.${userId},email.eq.${userEmail}`)
        .order('created_date', { ascending: false })
        .limit(5);

      if (teamsError) throw teamsError;

      const invitedPoolIds = new Set(transformedPools.map(pool => pool.id));
      const teamPoolIds = new Set(teamsData.map(team => team.pool.id));
      const userPoolIds = [...new Set([...invitedPoolIds, ...teamPoolIds])];

      let leaderboardsData = [];
      if (userPoolIds.length > 0) {
        const { data, error: leaderboardsError } = await supabase
          .from('pool')
          .select(`
            id, name, all_players_completed, tournament_status,
            tournament_fixture,
            tournamentsFixture:tournament_fixture ( id, tournament_id )
          `)
          .in('id', userPoolIds)
          .order('created_date', { ascending: false })
          .limit(5);

        if (leaderboardsError) throw leaderboardsError;
        leaderboardsData = data || [];
        // Reuse the same team count map
        const { data: lbTeamCounts } = await supabase
          .from('team')
          .select('pool')
          .in('pool', userPoolIds);
        const lbCountMap = {};
        (lbTeamCounts || []).forEach(t => { lbCountMap[t.pool] = (lbCountMap[t.pool] || 0) + 1; });
        leaderboardsData.forEach(p => { p.teamCount = lbCountMap[p.id] || 0; });
      }

      setPools(transformedPools);
      setTeams(teamsData || []);
      setTournaments(leaderboardsData);
    } catch (error) {
      await logError({
        filePath: 'DashboardComponents/Dashboard.js',
        functionName: 'fetchDashboardData',
        errorType: 'Dashboard Data Fetch Error',
        errorMessage: error.message,
        stackTrace: error.stack,
        requestData: { userId, userEmail }
      });
      toast.error('Error loading dashboard', { theme: "dark" });
    } finally {
      setLoading(false);
    }
  }, [userId, userEmail]);

  useEffect(() => {
    fetchDashboardData();

    const channel = supabase
      .channel('dashboard_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'InviteLink', filter: `person=eq.${userId}` }, () => fetchDashboardData())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'pool' }, () => fetchDashboardData())
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [userId, fetchDashboardData]);

  const LoadingSkeleton = () => (
    <div className="space-y-4">
      {[1, 2, 3].map(i => (
        <div key={i} className="animate-pulse">
          <div className="flex items-center gap-4 p-4">
            <div className="w-10 h-10 bg-gray-200 rounded-xl" />
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4" />
              <div className="h-3 bg-gray-100 rounded w-1/2" />
            </div>
            <div className="h-6 w-20 bg-gray-200 rounded-full" />
          </div>
        </div>
      ))}
    </div>
  );

  const EmptyState = ({ icon, text, action }) => (
    <div className="flex flex-col items-center justify-center py-12 px-4">
      <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
        {icon}
      </div>
      <p className="text-gray-400 text-sm text-center mb-4">{text}</p>
      {action}
    </div>
  );

  const ItemRow = ({ to, icon, iconBg, title, subtitle, badge }) => (
    <Link to={to} className="flex items-center gap-4 p-4 hover:bg-gray-50 rounded-xl transition-all group">
      <div className={`w-10 h-10 ${iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-gray-900 truncate">{title}</p>
        {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-2">
        {badge}
        <IoChevronForward className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors" />
      </div>
    </Link>
  );

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 md:pt-24">
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-primary to-primary-light rounded-2xl p-8 mb-8 shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white tracking-tight">
              Welcome back, {userName || 'Golfer'}
            </h1>
            <p className="text-green-200 mt-1">Manage your pools and track your teams</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="hidden md:flex items-center gap-2 bg-white text-primary font-bold py-3 px-6 rounded-xl hover:bg-green-50 transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5"
          >
            <FaPlus className="w-3.5 h-3.5" /> New Pool
          </button>
        </div>
      </div>

      {/* Mobile New Pool Button */}
      <button
        onClick={() => setShowModal(true)}
        className="md:hidden w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary-hover text-white font-bold py-3.5 px-6 rounded-xl transition-all shadow-md mb-6"
      >
        <FaPlus className="w-3.5 h-3.5" /> Create New Pool
      </button>

      {/* Dashboard Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-24 md:mb-8">
        {/* My Pools */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2.5">
              <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                <FaUsers className="w-4 h-4 text-primary" />
              </div>
              My Pools
            </h2>
            <Link to="/pools" className="text-sm font-semibold text-primary hover:text-primary-hover transition-colors">
              View All
            </Link>
          </div>
          <div className="p-2">
            {loading ? <LoadingSkeleton /> : pools.length === 0 ? (
              <EmptyState
                icon={<FaUsers className="w-6 h-6 text-gray-300" />}
                text="No pools yet. Create one to get started!"
                action={
                  <button onClick={() => setShowModal(true)} className="text-sm font-semibold text-primary hover:text-primary-hover">
                    + Create Pool
                  </button>
                }
              />
            ) : (
              pools.map(pool => (
                <ItemRow
                  key={pool.id}
                  to={`/pool/${pool.id}`}
                  icon={<FaUsers className="w-4 h-4 text-primary" />}
                  iconBg="bg-primary/10"
                  title={pool.name}
                  subtitle={`${pool.teamCount || 0} teams`}
                  badge={<StatusBadge status={pool.tournament_status || 'Not Started'} />}
                />
              ))
            )}
          </div>
        </div>

        {/* My Teams */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2.5">
              <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center">
                <IoGolfOutline className="w-4 h-4 text-emerald-600" />
              </div>
              My Teams
            </h2>
            <Link to="/teams" className="text-sm font-semibold text-primary hover:text-primary-hover transition-colors">
              View All
            </Link>
          </div>
          <div className="p-2">
            {loading ? <LoadingSkeleton /> : teams.length === 0 ? (
              <EmptyState
                icon={<IoGolfOutline className="w-6 h-6 text-gray-300" />}
                text="No teams yet"
              />
            ) : (
              teams.map(team => (
                <ItemRow
                  key={team.id}
                  to={`/pool/${team.pool?.id}/team/${team.id}`}
                  icon={<IoGolfOutline className="w-4 h-4 text-emerald-600" />}
                  iconBg="bg-emerald-50"
                  title={team.pool?.name}
                  subtitle={`Avg Rank: ${team.team_world_ranking_average || 'N/A'}`}
                  badge={<StatusBadge status={team.pool?.tournament_status || 'Not Started'} />}
                />
              ))
            )}
          </div>
        </div>

        {/* Leaderboards */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2.5">
              <div className="w-8 h-8 bg-yellow-50 rounded-lg flex items-center justify-center">
                <FaTrophy className="w-4 h-4 text-yellow-600" />
              </div>
              Leaderboards
            </h2>
            <Link to="/leaderboard" className="text-sm font-semibold text-primary hover:text-primary-hover transition-colors">
              View All
            </Link>
          </div>
          <div className="p-2">
            {loading ? <LoadingSkeleton /> : tournaments.length === 0 ? (
              <EmptyState
                icon={<FaTrophy className="w-6 h-6 text-gray-300" />}
                text="No leaderboards yet"
              />
            ) : (
              tournaments.map(t => (
                <ItemRow
                  key={t.id}
                  to={`/leaderboard/${t.id}`}
                  icon={<FaTrophy className="w-4 h-4 text-yellow-600" />}
                  iconBg="bg-yellow-50"
                  title={t.name}
                  subtitle={`${t.teamCount || 0} teams`}
                  badge={<StatusBadge status={t.tournament_status || 'Not Started'} />}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <PoolDetailsModal
          isOpen={showModal}
          onClose={() => { setShowModal(false); fetchDashboardData(); }}
          mode="insert"
          loggedInUser={Number(userId)}
        />
      )}
    </div>
  );
}

export default Dashboard;
